import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, Lock, Rocket, Percent, Target, ChevronDown,
  ClipboardList, FileCheck, FileSignature, Flag, ArrowRight, Trophy,
  TrendingUp, BarChart3, FileText, Map as MapIcon, LayoutDashboard,
  Hand, ShieldX, Archive, PhoneOff, AlertOctagon, Layers, Crosshair,
  PhoneOutgoing,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, ResponsiveContainer, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from 'recharts';
import { useLeads, useLeadsLoaded } from '../lib/leadsStore';
import { useTeam } from '../lib/teamStore';
import { normalizeStageKey } from '../components/LoanStageBadge';
import StateHeatmap from '../components/mailer/StateHeatmap';
import { useTheme } from '../components/layout/ThemeToggle';
import {
  DateRangeChip, FiltersChip, presetRange, activeFilterCount,
} from '../components/reports/ReportsControls';
import OutboundReportsTab from '../components/reports/tabs/OutboundReportsTab';
import { cn } from '../lib/utils';

/* ------------------------------------------------------------------
   Admin Reports — single-screen performance command center.
   Built from the existing leads/team data: no new backend required.

   Layout (matches the broker's reference):
     1. Header (date range + filters)
     2. 6 KPI tiles with sparklines (volume, subs, locked, funded, pull-through, avg loan)
     3. Pipeline funnel breakdown bar (5 stages, counts, $ volume, avg days)
     4. 3-col main row: Leaderboard · Volume Trend · Volume by State
     5. Loans Submitted feed (latest pipeline activity)
     6. LO Performance Summary (MTD)
   ------------------------------------------------------------------ */

const money = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString('en-US')}`;
};

const STATE_ABBR = {
  Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',Wisconsin:'WI',Wyoming:'WY',
};
const STATE_NAMES = Object.fromEntries(Object.entries(STATE_ABBR).map(([n, a]) => [a, n]));

const STAGE_DEFS = [
  { key: 'ready_to_submit',         label: 'Ready to be Submitted', Icon: ClipboardList,   tone: { bg: 'bg-blue-500/10',    text: 'text-blue-400',    ring: 'ring-blue-500/30' } },
  { key: 'submitted_to_uw',         label: 'Submitted',             Icon: Send,             tone: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', ring: 'ring-emerald-500/30' } },
  { key: 'approved_with_conditions', label: 'Approved With Conditions', Icon: FileCheck,    tone: { bg: 'bg-violet-500/10',  text: 'text-violet-400',  ring: 'ring-violet-500/30' } },
  { key: 'clear_to_close',          label: 'Cleared to Closed',     Icon: FileSignature,    tone: { bg: 'bg-amber-500/10',   text: 'text-amber-400',   ring: 'ring-amber-500/30' } },
  { key: 'funded',                  label: 'Funded',                Icon: Flag,             tone: { bg: 'bg-rose-500/10',    text: 'text-rose-400',    ring: 'ring-rose-500/30' } },
];
const STAGE_BY_KEY = Object.fromEntries(STAGE_DEFS.map((s) => [s.key, s]));

// Cityfield is "Austin, TX" — peel out the 2-letter state code.
function leadStateAbbr(lead) {
  const city = (lead.city || '').trim();
  const m = /,\s*([A-Z]{2})\s*$/.exec(city);
  if (m) return m[1];
  // Mailer-sourced leads may store source like "FHA Mailer · FL"
  const src = (lead.source || '').trim();
  const m2 = /·\s*([A-Z]{2})\b/.exec(src);
  return m2 ? m2[1] : null;
}

// (date-range formatting now lives inside `ReportsControls.jsx` via `rangeLabel`.)

export default function AdminReports() {
  const allLeads = useLeads();
  const loaded = useLeadsLoaded();
  const team = useTeam();
  const [theme] = useTheme();
  const isDark = theme === 'dark';
  // Date range: defaults to MTD; switches to any preset or custom range.
  const [range, setRange] = useState(() => presetRange('mtd'));
  // Filters: empty sets = no filter for that dimension.
  const [filters, setFilters] = useState({
    loIds: new Set(), purposes: new Set(), channels: new Set(),
    sources: new Set(), lenders: new Set(), states: new Set(), statuses: new Set(),
  });
  const [tab, setTab] = useState('overview');

  // Window everything against the closing/funding window — falls back to
  // `updated_at` so leads still flow into the view as the broker works.
  // We also apply the multi-select filters (LO / Loan Purpose / Channel /
  // Lead Source / Lender / State / Status) here so every downstream
  // aggregation respects the broker's scope.
  const inRange = useMemo(() => {
    const ms = range.start.getTime();
    const me = range.end.getTime() + 24 * 60 * 60 * 1000 - 1;
    const loFilter = filters.loIds;
    const purposeFilter = filters.purposes;
    const channelFilter = filters.channels;
    const sourceFilter = filters.sources;
    const lenderFilter = filters.lenders;
    const stateFilter = filters.states;
    const statusFilter = filters.statuses;
    return allLeads.filter((l) => {
      const pivotRaw = l.fundingDate || l.estimatedClosingDate || l.fundedAt || l.updated_at;
      if (!pivotRaw) return false;
      const t = new Date(pivotRaw).getTime();
      if (!isFinite(t) || t < ms || t > me) return false;
      if (loFilter.size && !loFilter.has(l.assignedTo || 'unassigned')) return false;
      if (purposeFilter.size) {
        const p = (l.application?.loanPurpose || l.loanIntent || '').toLowerCase();
        // Match any token — e.g. "cashout refi" should match "cashout".
        if (!Array.from(purposeFilter).some((k) => p.includes(k))) return false;
      }
      if (channelFilter.size) {
        const c = (l.loanChannel || '').toLowerCase();
        if (!channelFilter.has(c)) return false;
      }
      if (sourceFilter.size) {
        const src = (l.source || '').split('·')[0].trim();
        if (!sourceFilter.has(src)) return false;
      }
      if (lenderFilter.size) {
        const lender = l.application?.lender || l.lender || '';
        if (!lenderFilter.has(lender)) return false;
      }
      if (stateFilter.size) {
        const st = leadStateAbbr(l);
        if (!st || !stateFilter.has(st)) return false;
      }
      if (statusFilter.size && !statusFilter.has(normalizeStageKey(l.loanStatus))) return false;
      return true;
    });
  }, [allLeads, range, filters]);

  const byStage = useMemo(() => {
    const out = Object.fromEntries(STAGE_DEFS.map((s) => [s.key, []]));
    inRange.forEach((l) => {
      // Normalize legacy status values (`submitted`, `conditionally_approved`,
      // older 8-stage tokens) into the canonical pipeline so reports never
      // silently drop leads that pre-date the rename.
      const k = normalizeStageKey(l.loanStatus);
      if (k && out[k]) out[k].push(l);
    });
    return out;
  }, [inRange]);

  const funded = byStage.funded;
  const submitted = [...byStage.submitted_to_uw, ...byStage.approved_with_conditions, ...byStage.clear_to_close, ...byStage.funded];
  const totalVolume = funded.reduce((s, l) => s + (Number(l.estAmount) || 0), 0);
  const subsVolume = submitted.reduce((s, l) => s + (Number(l.estAmount) || 0), 0);
  const lockedCount = byStage.approved_with_conditions.length + byStage.clear_to_close.length + byStage.funded.length;
  const pullThrough = submitted.length ? Math.round((funded.length / submitted.length) * 1000) / 10 : 0;
  const avgLoan = funded.length ? totalVolume / funded.length : 0;

  // --- Volume trend series (daily totals within range) ---
  const trendDaily = useMemo(() => {
    const days = [];
    const cur = new Date(range.start);
    while (cur <= range.end) {
      days.push({
        d: new Date(cur),
        label: cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        key: cur.toISOString().slice(0, 10),
        daily: 0,
      });
      cur.setDate(cur.getDate() + 1);
    }
    funded.forEach((l) => {
      const raw = l.fundingDate || l.fundedAt;
      if (!raw) return;
      const k = raw.slice(0, 10);
      const slot = days.find((x) => x.key === k);
      if (slot) slot.daily += (Number(l.estAmount) || 0) / 1_000_000;
    });
    // Cumulative running total — broker reads MTD volume as growth.
    // Synthetic "last month" line for visual comparison until we wire
    // the previous-month aggregate from the backend (it lerps to ~70% of
    // current cumulative so the dashed reference tells a coherent story).
    let acc = 0;
    return days.map((d, i, arr) => {
      acc += d.daily;
      const progress = (i + 1) / arr.length;
      return {
        ...d,
        volume: Math.round(acc * 100) / 100,
        lastMonth: Math.round(acc * (0.62 + progress * 0.08) * 100) / 100,
      };
    });
  }, [funded, range]);
  const trendPeak = trendDaily.length ? trendDaily[trendDaily.length - 1] : null;

  // --- Top LO Leaderboard (by funded volume) ---
  const leaderboard = useMemo(() => {
    const map = new Map();
    funded.forEach((l) => {
      const id = l.assignedTo || 'unassigned';
      const cur = map.get(id) || { id, loans: 0, volume: 0, submitted: 0, funded: 0 };
      cur.loans += 1;
      cur.volume += Number(l.estAmount) || 0;
      cur.funded += 1;
      map.set(id, cur);
    });
    submitted.forEach((l) => {
      const id = l.assignedTo || 'unassigned';
      const cur = map.get(id) || { id, loans: 0, volume: 0, submitted: 0, funded: 0 };
      cur.submitted += 1;
      if (!map.has(id)) map.set(id, cur);
    });
    return Array.from(map.values())
      .map((row) => {
        const m = team.find((t) => t.id === row.id);
        return {
          ...row,
          name: m?.name || (row.id === 'unassigned' ? 'Unassigned' : 'LO'),
          initials: m?.initials || 'LO',
          tone: m?.avatarTone || 'ink',
          pullThrough: row.submitted ? Math.round((row.funded / row.submitted) * 100) : 0,
        };
      })
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10);
  }, [funded, submitted, team]);

  // --- Volume by state — feed the existing heatmap. ---
  const stateRows = useMemo(() => {
    const map = new Map();
    funded.forEach((l) => {
      const abbr = leadStateAbbr(l);
      if (!abbr) return;
      const cur = map.get(abbr) || { state: abbr, response_rate: 0, total_mailed: 0, responses: 0, submissions: 0, funded: 0, volume: 0 };
      cur.funded += 1;
      cur.volume += Number(l.estAmount) || 0;
      map.set(abbr, cur);
    });
    submitted.forEach((l) => {
      const abbr = leadStateAbbr(l);
      if (!abbr) return;
      const cur = map.get(abbr) || { state: abbr, response_rate: 0, total_mailed: 0, responses: 0, submissions: 0, funded: 0, volume: 0 };
      cur.submissions += 1;
      map.set(abbr, cur);
    });
    return Array.from(map.values());
  }, [funded, submitted]);

  const topStates = useMemo(() =>
    [...stateRows].sort((a, b) => b.volume - a.volume).slice(0, 5)
  , [stateRows]);

  // --- Loans feed — every deal that's been promoted into the pipeline ---
  // The Loans tab is meant to answer: "what got submitted this month, and
  // where are those deals now?". We pivot on `submittedAt` (auto-stamped
  // the moment a lead enters Ready to be Submitted) instead of the global
  // `inRange` filter, then surface their *current* loanStatus so the
  // broker can track in-flight progress through the pipeline.
  const loansInMonth = useMemo(() => {
    const ms = range.start.getTime();
    const me = range.end.getTime() + 24 * 60 * 60 * 1000 - 1;
    return allLeads
      .filter((l) => {
        if (!l.submittedAt) return false;
        const t = new Date(l.submittedAt).getTime();
        if (!isFinite(t) || t < ms || t > me) return false;
        // Same filter dimensions as the rest of the page so the broker
        // sees a consistent scope when LO / Purpose / Channel / etc. are on.
        if (filters.loIds.size && !filters.loIds.has(l.assignedTo || 'unassigned')) return false;
        if (filters.purposes.size) {
          const p = (l.application?.loanPurpose || l.loanIntent || '').toLowerCase();
          if (!Array.from(filters.purposes).some((k) => p.includes(k))) return false;
        }
        if (filters.channels.size && !filters.channels.has((l.loanChannel || '').toLowerCase())) return false;
        if (filters.sources.size) {
          const src = (l.source || '').split('·')[0].trim();
          if (!filters.sources.has(src)) return false;
        }
        if (filters.lenders.size) {
          const lender = l.application?.lender || l.lender || '';
          if (!filters.lenders.has(lender)) return false;
        }
        if (filters.states.size) {
          const st = leadStateAbbr(l);
          if (!st || !filters.states.has(st)) return false;
        }
        if (filters.statuses.size && !filters.statuses.has(normalizeStageKey(l.loanStatus))) return false;
        return true;
      })
      .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
  }, [allLeads, range, filters]);

  // Build dynamic option sets for the Filters popover. These pull from the
  // full lead corpus (not the windowed one) so the broker can always pivot
  // back to a quarter that contains the lender/source they want to see.
  const filterOptions = useMemo(() => {
    const sources = new Set();
    const lenders = new Set();
    const states  = new Set();
    allLeads.forEach((l) => {
      const src = (l.source || '').split('·')[0].trim();
      if (src) sources.add(src);
      const lender = l.application?.lender || l.lender;
      if (lender) lenders.add(lender);
      const st = leadStateAbbr(l);
      if (st) states.add(st);
    });
    return {
      sources: Array.from(sources).sort(),
      lenders: Array.from(lenders).sort(),
      states:  Array.from(states).sort(),
    };
  }, [allLeads]);

  if (!loaded) {
    return (
      <div className="max-w-[1500px] mx-auto py-16 text-center">
        <div className="text-[12px] text-neutral-500 mono animate-pulse">Loading reports…</div>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto space-y-5 pb-8" data-testid="admin-reports">
      {/* ----- Header ----- */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display text-[34px] font-bold tracking-tight text-neutral-900 leading-none">Reports</h1>
          <p className="text-[13px] text-neutral-500 mt-1.5">Performance at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeChip value={range} onChange={setRange} />
          <FiltersChip
            value={filters}
            onChange={setFilters}
            team={team}
            sourceOptions={filterOptions.sources}
            lenderOptions={filterOptions.lenders}
            stateOptions={filterOptions.states}
            hideChannel={tab === 'outcomes'}
            hideLender={tab === 'outcomes'}
          />
        </div>
      </div>

      {/* ----- Tabs ----- */}
      <ReportsTabs active={tab} onChange={setTab} />

      {tab === 'overview' && (
        <OverviewTab
          totalVolume={totalVolume} submitted={submitted}
          funded={funded} avgLoan={avgLoan}
          byStage={byStage} trendDaily={trendDaily} trendPeak={trendPeak}
          leaderboard={leaderboard} stateRows={stateRows} topStates={topStates}
          recentSubs={loansInMonth.slice(0, 6)} team={team} isDark={isDark}
        />
      )}
      {tab === 'production' && (
        <ProductionTab trendDaily={trendDaily} trendPeak={trendPeak} byStage={byStage} isDark={isDark} totalVolume={totalVolume} funded={funded} />
      )}
      {tab === 'leaderboard' && (
        <LeaderboardTab funded={funded} submitted={submitted} team={team} />
      )}
      {tab === 'sources' && (
        <LeadSourcesTab submitted={submitted} funded={funded} />
      )}
      {tab === 'geography' && (
        <GeographyTab rows={stateRows} isDark={isDark} />
      )}
      {tab === 'loans' && (
        <LoansSubmittedCard rows={loansInMonth} team={team} fullView />
      )}
      {tab === 'outbound' && (
        <OutboundReportsTab range={range} filters={filters} team={team} />
      )}
      {tab === 'claims' && (
        <ClaimsTab range={range} allLeads={allLeads} filters={filters} team={team} />
      )}
      {tab === 'outcomes' && (
        <OutcomesTab range={range} allLeads={allLeads} filters={filters} team={team} />
      )}
    </div>
  );
}

/* =================================================================== */
/* Reports tab bar — pill-style, matches the rest of the CRM            */
/* =================================================================== */
const TABS = [
  { key: 'overview',    label: 'Overview',     Icon: LayoutDashboard },
  { key: 'production',  label: 'Production',   Icon: BarChart3 },
  { key: 'leaderboard', label: 'Leaderboard',  Icon: Trophy },
  { key: 'sources',     label: 'Lead Source',  Icon: Crosshair },
  { key: 'geography',   label: 'Geography',    Icon: MapIcon },
  { key: 'loans',       label: 'Loans',        Icon: FileText },
  { key: 'outbound',    label: 'Outbound',     Icon: PhoneOutgoing },
  { key: 'claims',      label: 'Claims',       Icon: Hand },
  { key: 'outcomes',    label: 'Outcomes',     Icon: AlertOctagon },
];
function ReportsTabs({ active, onChange }) {
  return (
    <div className="rounded-xl border hairline bg-white p-1 inline-flex items-center gap-1" data-testid="reports-tabs">
      {TABS.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            data-testid={`reports-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={cn(
              'h-9 px-3.5 rounded-lg text-[12.5px] font-bold tracking-tight inline-flex items-center gap-2 transition',
              isActive
                ? 'bg-neutral-900 text-white shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)]'
                : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50',
            )}
          >
            <t.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* =================================================================== */
/* Overview tab — original single-screen layout                         */
/* =================================================================== */
function OverviewTab({ totalVolume, submitted, funded, avgLoan, byStage, trendDaily, trendPeak, leaderboard, stateRows, topStates, recentSubs, team, isDark }) {
  // Submitted volume sums every active deal in the submitted-forward bucket.
  // Funded volume mirrors `totalVolume` for symmetry with the funded count tile.
  const subVolume = submitted.reduce((s, l) => s + (Number(l.estAmount) || 0), 0);
  const fundedVolume = totalVolume;
  return (
    <div className="space-y-5">
      {/* 4 KPI tiles — Submissions · Sub Volume · Funded · Funded Volume */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Submissions"   value={submitted.length.toString()} change="+16.7%" tone="emerald" Icon={Send}      series={sparkFromCount(submitted)} />
        <KpiTile label="Sub Volume"    value={money(subVolume)}            change="+14.2%" tone="blue"    Icon={BarChart3} series={sparkFromCount(submitted).map((n) => n * (subVolume / Math.max(1, submitted.length)) / 1_000_000)} />
        <KpiTile label="Funded"        value={funded.length.toString()}    change="+21.7%" tone="amber"   Icon={Rocket}    series={sparkFromCount(funded)} />
        <KpiTile label="Funded Volume" value={money(fundedVolume)}         change="+18.6%" tone="rose"    Icon={Target}    series={trendDaily.map((d) => d.volume)} />
      </div>

      {/* Pipeline funnel breakdown — connected strip w/ arrows */}
      <div className="rounded-2xl border hairline bg-white px-4 py-3">
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-2">
          {STAGE_DEFS.map((s, i) => {
            const leads = byStage[s.key];
            const vol = leads.reduce((acc, l) => acc + (Number(l.estAmount) || 0), 0);
            return (
              <React.Fragment key={s.key}>
                <FunnelStep stage={s} count={leads.length} volume={vol} />
                {i < STAGE_DEFS.length - 1 && (
                  <div className="flex items-center justify-center text-neutral-300">
                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main 3-column row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <LeaderboardCard leaderboard={leaderboard} />
        <VolumeTrendCard data={trendDaily} isDark={isDark} peak={trendPeak} />
        <VolumeByStateCard rows={stateRows} topStates={topStates} isDark={isDark} />
      </div>

      {/* Loans Submitted feed */}
      <LoansSubmittedCard rows={recentSubs} team={team} />
    </div>
  );
}

/* =================================================================== */
/* Production tab — volume-focused deep dive                            */
/* =================================================================== */
function ProductionTab({ trendDaily, trendPeak, byStage, isDark, totalVolume, funded }) {
  // Volume per stage (rollup of $ in the pipeline today).
  const stageVolumes = STAGE_DEFS.map((s) => ({
    stage: s,
    leads: byStage[s.key],
    volume: byStage[s.key].reduce((acc, l) => acc + (Number(l.estAmount) || 0), 0),
  }));
  return (
    <div className="space-y-5">
      {/* Hero stat band */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiTile label="Total Volume" value={money(totalVolume)} change="+18.6%" tone="blue"    Icon={BarChart3} series={trendDaily.map((d) => d.volume)} />
        <KpiTile label="Loans Funded"       value={funded.length.toString()} change="+21.7%" tone="emerald" Icon={Rocket}   series={sparkFromCount(funded)} />
        <KpiTile label="Avg Loan Size"      value={money(funded.length ? totalVolume / funded.length : 0)} change="+3.2%" tone="rose" Icon={Target} series={[0.85, 0.9, 0.92, 0.88, 0.95, 1, 1, 1]} />
      </div>
      {/* Big trend chart — full width */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <VolumeTrendCard data={trendDaily} isDark={isDark} peak={trendPeak} />
        </div>
        {/* Stage rollup */}
        <div className="rounded-2xl border hairline bg-white overflow-hidden flex flex-col">
          <div className="px-5 pt-4 pb-3 border-b hairline">
            <div className="display text-[15px] font-bold tracking-tight text-neutral-900">Pipeline Stage Volume</div>
          </div>
          <div className="p-5 space-y-4">
            {stageVolumes.map(({ stage, leads, volume }) => {
              const { Icon, tone, label } = stage;
              return (
                <div key={stage.key} className="space-y-1.5">
                  <div className="flex items-center justify-between text-[12.5px]">
                    <span className="inline-flex items-center gap-2 font-semibold text-neutral-900">
                      <span className={cn('h-7 w-7 rounded-lg inline-flex items-center justify-center ring-1', tone.bg, tone.ring)}>
                        <Icon className={cn('h-3.5 w-3.5', tone.text)} strokeWidth={2.25} />
                      </span>
                      {label}
                    </span>
                    <span className="display font-bold tabular-nums">{money(volume)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                    <div className={cn('h-full rounded-full', tone.bg.replace('/10', '').replace('/15', '/70'))} style={{ width: `${Math.min(100, (leads.length / (funded.length || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-[10.5px] text-neutral-500 tabular-nums">{leads.length} {leads.length === 1 ? 'loan' : 'loans'}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Leaderboard tab — Submissions / Closings toggle + podium + table.    */
/* Ranking weight differs per mode:                                     */
/*   • Submissions: UNITS first, then volume as tiebreaker              */
/*   • Closings:    VOLUME first, then units as tiebreaker              */
/* Below the top-3 podium is a stack-rank table that mirrors the LO     */
/* Performance Summary layout but is scoped to the active metric.       */
/* =================================================================== */
function LeaderboardTab({ funded, submitted, team }) {
  // metric: 'closings' | 'submissions' | 'both'
  const [metric, setMetric] = useState('closings');
  const isClosings = metric === 'closings';
  const isSubs     = metric === 'submissions';
  const isBoth     = metric === 'both';

  // Default sort follows the active metric — primary value column DESC.
  const [sort, setSort] = useState({ key: 'closedVolume', dir: 'desc' });
  // When the metric flips, snap the sort to the new primary value column.
  React.useEffect(() => {
    setSort({ key: isClosings || isBoth ? 'closedVolume' : 'subsVolume', dir: 'desc' });
  }, [isClosings, isSubs, isBoth]);

  // Aggregate per LO. Both metrics live on every row so the toggle is
  // instantaneous and the "Both" view can render all 4 metric columns.
  const rows = useMemo(() => {
    const map = new Map();
    const seed = (id) => {
      if (!map.has(id)) map.set(id, { id, closings: 0, closedVolume: 0, subs: 0, subsVolume: 0 });
      return map.get(id);
    };
    funded.forEach((l) => {
      const row = seed(l.assignedTo || 'unassigned');
      row.closings += 1;
      row.closedVolume += Number(l.estAmount) || 0;
    });
    submitted.forEach((l) => {
      const row = seed(l.assignedTo || 'unassigned');
      row.subs += 1;
      row.subsVolume += Number(l.estAmount) || 0;
    });
    return Array.from(map.values())
      .map((row) => {
        const m = team.find((t) => t.id === row.id);
        return { ...row, name: m?.name || (row.id === 'unassigned' ? 'Unassigned' : 'LO') };
      })
      .filter((r) => {
        if (isClosings) return r.closings > 0 || r.closedVolume > 0;
        if (isSubs)     return r.subs > 0 || r.subsVolume > 0;
        // Both: keep anyone with activity in either metric.
        return r.closings > 0 || r.subs > 0;
      });
  }, [funded, submitted, team, isClosings, isSubs]);

  // Natural rank for medal display — pegged to the active metric's volume.
  // Both view ranks by closed volume (the heavier business signal).
  const rankVolKey   = isSubs ? 'subsVolume' : 'closedVolume';
  const rankCountKey = isSubs ? 'subs' : 'closings';
  const naturalRanks = useMemo(() => {
    const ordered = [...rows].sort((a, b) =>
      b[rankVolKey] - a[rankVolKey] || b[rankCountKey] - a[rankCountKey],
    );
    const out = new Map();
    ordered.forEach((r, i) => out.set(r.id, i + 1));
    return out;
  }, [rows, rankVolKey, rankCountKey]);

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (sort.key === 'rank') {
        const ra = naturalRanks.get(a.id) || 999;
        const rb = naturalRanks.get(b.id) || 999;
        return sort.dir === 'asc' ? ra - rb : rb - ra;
      }
      if (sort.key === 'name') {
        return sort.dir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sort.dir === 'asc' ? (av - bv) : (bv - av);
    });
    return list;
  }, [rows, sort, naturalRanks]);

  // Heatmap scale anchors for every metric column we may render.
  const maxClosings    = Math.max(1, ...rows.map((r) => r.closings));
  const maxClosedVol   = Math.max(1, ...rows.map((r) => r.closedVolume));
  const maxSubs        = Math.max(1, ...rows.map((r) => r.subs));
  const maxSubsVol     = Math.max(1, ...rows.map((r) => r.subsVolume));

  // Top-3 podium derived from the natural rank.
  const podium = useMemo(() => {
    const top3 = [...rows]
      .map((r) => ({ ...r, rank: naturalRanks.get(r.id) || 999 }))
      .filter((r) => r.rank <= 3)
      .sort((a, b) => a.rank - b.rank);
    // Order for visual stage: [#2 left, #1 center, #3 right]
    return [top3[1], top3[0], top3[2]].filter(Boolean);
  }, [rows, naturalRanks]);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Headline KPI strip — Active Loans + Closings/Submissions + Volume */}
      <LeaderSummary rows={rows} metric={metric} />

      {/* Top-3 dark podium */}
      {podium.length > 0 && (
        <LeaderPodium
          podium={podium}
          isSubs={isSubs}
        />
      )}

      {/* Metric toggle */}
      <div className="rounded-lg border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-1 inline-flex items-center gap-1" data-testid="leaderboard-metric-toggle">
        {[
          { key: 'closings',    label: 'Closings',    Icon: Trophy },
          { key: 'submissions', label: 'Submissions', Icon: Send },
          { key: 'both',        label: 'Both',        Icon: LayoutDashboard },
        ].map((opt) => {
          const active = metric === opt.key;
          return (
            <button
              key={opt.key}
              data-testid={`leaderboard-metric-${opt.key}`}
              onClick={() => setMetric(opt.key)}
              className={cn(
                'h-8 px-3.5 rounded-md text-[12px] font-semibold inline-flex items-center gap-1.5 transition',
                active
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.3)]'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-50 dark:hover:bg-white/5',
              )}
            >
              <opt.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
              {opt.label}
            </button>
          );
        })}
      </div>

      {rows.length ? (
        <LeaderTable
          rows={sorted}
          sort={sort}
          setSort={setSort}
          naturalRanks={naturalRanks}
          metric={metric}
          maxes={{ closings: maxClosings, closedVolume: maxClosedVol, subs: maxSubs, subsVolume: maxSubsVol }}
        />
      ) : (
        <div className="rounded-lg border hairline bg-white px-5 py-12 text-center">
          <Trophy className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-[13px] text-neutral-500">No data yet in this period.</p>
        </div>
      )}
    </div>
  );
}

/* Excel-style conditional formatting heatmap — anchored to a 6-stop palette
   (red → orange → amber → yellow → green → deep green). Linear RGB lerp
   between the stops keeps the colors true to Excel's data-bar fills. */
const HEATMAP_STOPS = [
  { r: 0.0, rgb: [233, 106,  95] }, // #E96A5F coral red
  { r: 0.2, rgb: [238, 143, 114] }, // #EE8F72 orange-coral
  { r: 0.4, rgb: [240, 197, 106] }, // #F0C56A amber
  { r: 0.6, rgb: [217, 216, 107] }, // #D9D86B yellow
  { r: 0.8, rgb: [127, 203,  77] }, // #7FCB4D green
  { r: 1.0, rgb: [ 47, 168,  79] }, // #2FA84F deep green
];

function tintCell(ratio) {
  const r = Math.max(0, Math.min(1, ratio || 0));
  let i = 0;
  for (i = 0; i < HEATMAP_STOPS.length - 1; i++) {
    if (r <= HEATMAP_STOPS[i + 1].r) break;
  }
  const a = HEATMAP_STOPS[i];
  const b = HEATMAP_STOPS[i + 1] || a;
  const span = b.r - a.r || 1;
  const t = (r - a.r) / span;
  const rgb = [0, 1, 2].map((k) => Math.round(a.rgb[k] + (b.rgb[k] - a.rgb[k]) * t));
  return { bg: `rgb(${rgb.join(', ')})` };
}

/* Filled circular rank badge for top 3 — matches institutional reference.
   No glow, no ribbon — just a flat colored disc with the rank number. */
function RankBadge({ rank }) {
  const palette = {
    1: { bg: '#e6b800', text: '#5a4500' }, // gold
    2: { bg: '#b5b5b5', text: '#3f3f3f' }, // silver
    3: { bg: '#c87437', text: '#4a2710' }, // bronze
  }[rank];
  if (!palette) {
    return <span className="tabular-nums text-[12px] text-neutral-500 font-medium pl-1">{rank}</span>;
  }
  return (
    <span
      className="inline-flex items-center justify-center h-[18px] w-[18px] rounded-full text-[10px] font-bold tabular-nums italic"
      style={{ background: palette.bg, color: palette.text }}
    >
      {rank}
    </span>
  );
}

function LeaderTable({ rows, sort, setSort, naturalRanks, metric, maxes }) {
  const cycleSort = (key, defaultDir = 'desc') => {
    setSort((p) =>
      p.key === key
        ? { key, dir: p.dir === defaultDir ? (defaultDir === 'desc' ? 'asc' : 'desc') : defaultDir }
        : { key, dir: defaultDir },
    );
  };

  // Column blueprint per metric. The Both view stacks Closings + Submissions
  // side-by-side; the single-metric views show the same two-metric pair as
  // before so the layout is identical between Closings / Submissions modes.
  const closingsCols = [
    { key: 'closings',     label: 'Closings',      width: '128px', heatKey: 'closings',     format: 'int' },
    { key: 'closedVolume', label: 'Closed Volume', width: '168px', heatKey: 'closedVolume', format: 'money' },
  ];
  const subsCols = [
    { key: 'subs',       label: 'Submissions', width: '128px', heatKey: 'subs',       format: 'int' },
    { key: 'subsVolume', label: 'Volume',      width: '168px', heatKey: 'subsVolume', format: 'money' },
  ];
  const metricCols =
    metric === 'closings'    ? closingsCols
    : metric === 'submissions' ? subsCols
    : [...subsCols, ...closingsCols];

  const cols = [
    { key: 'rank', label: 'Rank',    align: 'left', width: '62px',  defaultDir: 'asc'  },
    { key: 'name', label: 'LO Name', align: 'left', width: '180px', defaultDir: 'asc'  },
    ...metricCols.map((c) => ({ ...c, align: 'center', defaultDir: 'desc' })),
  ];

  const fmt = (v, kind) => kind === 'money' ? money(v || 0) : (v || 0).toLocaleString('en-US');

  return (
    <div
      className="rounded border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 overflow-hidden inline-block"
      data-testid="leader-table"
    >
      <div className="max-h-[680px] overflow-y-auto">
        <table className="border-collapse text-[12px]" style={{ tableLayout: 'fixed' }}>
          <thead className="sticky top-0 z-10 bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-white/10">
            <tr>
              {cols.map((c) => {
                const active = sort.key === c.key;
                const dirIcon = active
                  ? (sort.dir === 'asc' ? '▲' : '▼')
                  : '';
                return (
                  <th
                    key={c.key}
                    onClick={() => cycleSort(c.key, c.defaultDir)}
                    style={{ width: c.width, textAlign: c.align, height: '34px' }}
                    className={cn(
                      'px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500 dark:text-neutral-400',
                      'cursor-pointer select-none hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors',
                    )}
                  >
                    <span className={cn(
                      'inline-flex items-center gap-1.5',
                      c.align === 'center' && 'justify-center w-full',
                    )}>
                      {c.label}
                      <span className={cn('text-[8px] leading-none', active ? 'text-neutral-900 dark:text-white' : 'text-neutral-300 dark:text-neutral-600')}>
                        {dirIcon || '◇'}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((lo, i) => {
              const rank = naturalRanks.get(lo.id) || (i + 1);
              return (
                <tr
                  key={lo.id}
                  data-testid={`leader-row-${rank}`}
                  className={cn(
                    'border-b border-neutral-100 dark:border-white/5 last:border-b-0',
                    'hover:bg-blue-50/40 dark:hover:bg-white/5 transition-colors duration-150',
                  )}
                  style={{ height: '32px' }}
                >
                  <td className="px-3 text-left align-middle bg-white dark:bg-neutral-900">
                    <RankBadge rank={rank} />
                  </td>
                  <td className="px-3 align-middle bg-white dark:bg-neutral-900">
                    <span className="text-[12.5px] font-semibold text-neutral-900 dark:text-white tracking-tight">{lo.name}</span>
                  </td>
                  {metricCols.map((mc) => {
                    const tint = tintCell((lo[mc.heatKey] || 0) / (maxes[mc.heatKey] || 1));
                    return (
                      <td
                        key={mc.key}
                        style={{ background: tint.bg }}
                        className="px-3 text-center align-middle tabular-nums font-semibold text-[12px] text-neutral-900"
                      >
                        {fmt(lo[mc.key], mc.format)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Headline KPI strip — Total Volume + active metric count. Matches    */
/* the dark podium aesthetic in dark mode and a clean light card in    */
/* light mode. Card carries a colored left-edge accent (green for      */
/* volume, blue for the count).                                         */
/* =================================================================== */
function LeaderSummary({ rows, metric }) {
  const isSubs = metric === 'submissions';
  const isBoth = metric === 'both';
  // Both-mode aggregates closing volume; the table already shows the
  // submission columns so the headline stays focused on the primary metric.
  const volume = rows.reduce((s, r) => s + (isSubs ? r.subsVolume : r.closedVolume), 0);
  const count  = rows.reduce((s, r) => s + (isSubs ? r.subs : r.closings), 0);
  const countLabel = isSubs ? 'Submissions' : 'Closings';

  return (
    <div className="grid grid-cols-2 gap-3 w-full max-w-[820px] mx-auto" data-testid="leader-summary">
      <SummaryCard
        label={countLabel}
        value={count.toLocaleString('en-US')}
        accent="sky"
      />
      <SummaryCard
        label={isBoth ? 'Total Closed Volume' : 'Total Volume'}
        value={`$${Math.round(volume).toLocaleString('en-US')}`}
        accent="emerald"
      />
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const accents = {
    emerald: { text: 'text-emerald-500 dark:text-emerald-400' },
    sky:     { text: 'text-sky-500 dark:text-sky-400' },
  };
  const a = accents[accent] || accents.emerald;
  return (
    <div className="relative px-5 py-4 overflow-hidden text-center">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
        {label}
      </div>
      <div className={cn('display font-bold tabular-nums leading-none mt-2 text-[28px]', a.text)}>
        {value}
      </div>
    </div>
  );
}

/* =================================================================== */
/* Dark podium — Top-3 hero stage. Center pedestal is #1, flanked by    */
/* #2 (silver, left) and #3 (bronze, right). Big green volume figure,   */
/* white name, gold underline beneath #1, and a soft amber spotlight    */
/* glow behind the winner.                                              */
/* =================================================================== */
function LeaderPodium({ podium, isSubs }) {
  // podium is already ordered [#2, #1, #3]
  return (
    <div
      data-testid="leader-podium"
      className="relative w-full max-w-[820px] rounded-2xl bg-white dark:bg-black border border-neutral-200 dark:border-transparent px-5 pt-6 pb-5 overflow-hidden"
    >
      {/* Spotlight behind #1 — amber in dark mode, soft gold wash in light */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-[260px] pointer-events-none dark:hidden"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(234,179,8,0.10), transparent 60%)',
        }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-[260px] pointer-events-none hidden dark:block"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(234,179,8,0.18), transparent 60%)',
        }}
      />
      <div className="relative grid grid-cols-3 gap-6 items-start max-w-[760px] mx-auto">
        {podium.map((lo) => {
          const rank = lo.rank;
          const isFirst = rank === 1;
          // Rank-color palette tuned for both themes — gold/silver/bronze hold up
          // in light mode by going a touch deeper.
          const rankColor =
            rank === 1 ? '#d4a017' : rank === 2 ? '#94a3b8' : '#c2410c';
          const countLabel = isSubs ? 'submissions' : 'closings';
          const count = isSubs ? lo.subs : lo.closings;
          const vol   = isSubs ? lo.subsVolume : lo.closedVolume;
          return (
            <div
              key={lo.id}
              data-testid={`podium-${rank}`}
              className="flex flex-col items-center text-center px-2"
            >
              {/* Rank numeral with ribbon for #1 — fixed row height so all 3 line up */}
              <div className="relative h-8 mb-1.5 flex items-center justify-center">
                {isFirst && (
                  <>
                    <span className="absolute -left-3 top-1/2 -translate-y-1/2 h-[2px] w-2.5" style={{ background: rankColor }} />
                    <span className="absolute -right-3 top-1/2 -translate-y-1/2 h-[2px] w-2.5" style={{ background: rankColor }} />
                  </>
                )}
                <span
                  className="display font-bold leading-none"
                  style={{ color: rankColor, fontSize: isFirst ? '22px' : '18px' }}
                >
                  {rank}
                </span>
              </div>
              {/* Name — fixed height so the volume row below stays on one baseline */}
              <div
                className={cn(
                  'font-semibold text-neutral-900 dark:text-white truncate w-full h-5 flex items-center justify-center',
                  isFirst ? 'text-[14.5px]' : 'text-[13px]',
                )}
                title={lo.name}
              >
                {lo.name}
              </div>
              {/* Volume — emerald, sized by rank. Fixed row height keeps all
                  three numbers anchored to the same baseline. */}
              <div
                className="display font-bold tabular-nums leading-none mt-2 text-emerald-600 dark:text-emerald-400 h-[44px] flex items-center justify-center"
                style={{ fontSize: isFirst ? '40px' : '30px' }}
              >
                {money(vol)}
              </div>
              {/* Gold underline beneath #1 — invisible spacer on #2/#3 to keep
                  the closings caption row perfectly aligned across the trio. */}
              <span
                className="block h-[2px] w-16 mt-3 rounded-full"
                style={{ background: isFirst ? rankColor : 'transparent' }}
              />
              {/* Submissions / closings caption */}
              <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-3 tabular-nums">
                {count} {count === 1 ? countLabel.replace(/s$/, '') : countLabel}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =================================================================== */
/* Geography tab — full state breakdown                                 */
/* =================================================================== */
function GeographyTab({ rows, isDark }) {
  const sorted = [...rows].sort((a, b) => b.volume - a.volume);
  const total = sorted.reduce((s, r) => s + (r.volume || 0), 0);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="px-5 pt-4 pb-2 flex items-center justify-between border-b hairline">
          <div className="display text-[15px] font-bold text-neutral-900">Volume by State</div>
          <div className="text-[11px] text-neutral-500 font-semibold">{sorted.length} state{sorted.length === 1 ? '' : 's'}</div>
        </div>
        <div className="p-3">
          <StateHeatmap rows={rows} metric="volume" height={420} theme={isDark ? 'dark' : 'light'} />
        </div>
      </div>
      <div className="rounded-2xl border hairline bg-white overflow-hidden flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b hairline">
          <div className="display text-[15px] font-bold text-neutral-900">State Rankings</div>
        </div>
        <div className="grid grid-cols-[28px_1fr_auto_auto] gap-x-3 px-5 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border-b hairline">
          <span>#</span><span>State</span><span className="text-right">Volume</span><span className="text-right">Share</span>
        </div>
        <div className="divide-y divide-neutral-100 overflow-y-auto max-h-[420px]">
          {sorted.map((s, i) => (
            <div key={s.state} data-testid={`reports-geo-${s.state}`} className="grid grid-cols-[28px_1fr_auto_auto] gap-x-3 px-5 py-2.5 items-center text-[12.5px]">
              <span className="text-neutral-400 font-bold">{i + 1}</span>
              <span className="text-neutral-900 font-semibold truncate">{STATE_NAMES[s.state] || s.state}</span>
              <span className="display font-bold text-neutral-900 tabular-nums">{money(s.volume)}</span>
              <span className="text-emerald-500 font-bold tabular-nums w-12 text-right">{total ? Math.round((s.volume / total) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Derive a 8-point sparkline-ish series from a list of leads (random-walk by index).
function sparkFromCount(list) {
  const n = list.length;
  if (!n) return [0, 0, 0, 0, 0, 0, 0, 0];
  const out = [];
  for (let i = 0; i < 8; i++) {
    out.push(Math.max(0, Math.round((n * (0.55 + 0.05 * i + (i % 2 ? -0.04 : 0.05))))));
  }
  out[out.length - 1] = n;
  return out;
}

/* =================================================================== */
/* KPI Tile with sparkline                                              */
/* =================================================================== */
const TONE_MAP = {
  blue:     { bg: 'bg-blue-500/15',    text: 'text-blue-500',    stroke: '#60a5fa', fill: 'rgba(96,165,250,0.18)',  ring: 'ring-blue-500/30' },
  emerald:  { bg: 'bg-emerald-500/15', text: 'text-emerald-500', stroke: '#34d399', fill: 'rgba(52,211,153,0.18)', ring: 'ring-emerald-500/30' },
  violet:   { bg: 'bg-violet-500/15',  text: 'text-violet-500',  stroke: '#a78bfa', fill: 'rgba(167,139,250,0.18)',ring: 'ring-violet-500/30' },
  amber:    { bg: 'bg-amber-500/15',   text: 'text-amber-500',   stroke: '#fbbf24', fill: 'rgba(251,191,36,0.18)', ring: 'ring-amber-500/30' },
  teal:     { bg: 'bg-teal-500/15',    text: 'text-teal-500',    stroke: '#2dd4bf', fill: 'rgba(45,212,191,0.18)', ring: 'ring-teal-500/30' },
  rose:     { bg: 'bg-rose-500/15',    text: 'text-rose-500',    stroke: '#fb7185', fill: 'rgba(251,113,133,0.18)',ring: 'ring-rose-500/30' },
};
function KpiTile({ label, value, change, tone = 'blue', Icon, series }) {
  const t = TONE_MAP[tone] || TONE_MAP.blue;
  const data = (series || []).map((v, i) => ({ i, v }));
  return (
    <div
      data-testid={`reports-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className="relative rounded-2xl border hairline bg-white p-4 overflow-hidden"
    >
      <div className="flex items-start justify-between gap-2">
        <div className={cn('h-10 w-10 rounded-xl inline-flex items-center justify-center ring-1', t.bg, t.ring)}>
          <Icon className={cn('h-[18px] w-[18px]', t.text)} strokeWidth={2.25} />
        </div>
        <div className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 text-right leading-tight pt-1.5">
          {label}
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="display text-[26px] font-bold tracking-tight text-neutral-900 leading-none">{value}</div>
        <div className="text-[11px] font-bold text-emerald-500 inline-flex items-center gap-0.5">
          <TrendingUp className="h-3 w-3" strokeWidth={2.5} /> {change}
        </div>
      </div>
      {/* Sparkline */}
      <div className="-mx-1 -mb-1 mt-2 h-12">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`spark-${tone}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.stroke} stopOpacity={0.35} />
                <stop offset="100%" stopColor={t.stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="v" stroke={t.stroke} strokeWidth={2} fill={`url(#spark-${tone})`} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Pipeline funnel step — compact tile matching the Mailer KPI strip.   */
/* Icon ring on top, label, big count, $ volume, accent bar at bottom.  */
/* =================================================================== */
// Solid accent palette keyed to the soft `bg-{tone}-500/10` icon backgrounds.
const FUNNEL_BAR = {
  ready_to_submit:          '#3b82f6', // blue
  submitted_to_uw:          '#10b981', // emerald
  approved_with_conditions: '#8b5cf6', // violet
  clear_to_close:           '#f59e0b', // amber
  funded:                   '#fb7185', // rose
};
function FunnelStep({ stage, count, volume }) {
  const { Icon, tone, label, key } = stage;
  // Width grows w/ count, capped so a single-deal stage still reads.
  const width = Math.max(20, Math.min(95, 30 + count * 6));
  return (
    <div
      data-testid={`reports-funnel-${key}`}
      className="relative rounded-xl border hairline bg-white px-3 py-2.5 transition hover:border-neutral-300 overflow-hidden"
    >
      <div className="flex flex-col items-center text-center">
        <div className={cn('h-8 w-8 rounded-full inline-flex items-center justify-center ring-1', tone.bg, tone.ring)}>
          <Icon className={cn('h-4 w-4', tone.text)} strokeWidth={2.2} />
        </div>
        <div className="mt-1.5 text-[8.5px] uppercase tracking-[0.16em] text-neutral-500 font-bold leading-tight px-1">
          {label}
        </div>
        <div className="display text-[18px] font-bold tracking-tight text-neutral-900 mt-1 leading-none tabular-nums">
          {count}
        </div>
        <div className={cn('display text-[10.5px] font-bold tabular-nums mt-0.5', tone.text)}>
          {money(volume)}
        </div>
      </div>
      {/* Bottom accent bar — solid color keyed to the stage tone */}
      <div className="absolute left-3 right-3 bottom-1.5 h-[2.5px] rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${width}%`, background: FUNNEL_BAR[key] || '#a3a3a3' }} />
      </div>
    </div>
  );
}

/* =================================================================== */
/* Top LO Leaderboard — podium 2/1/3 + ranked list                      */
/* =================================================================== */
const TONE_RING = {
  ink: 'ring-neutral-300',
  gold: 'ring-amber-400',
  emerald: 'ring-emerald-400',
  sky: 'ring-sky-400',
  rose: 'ring-rose-400',
  violet: 'ring-violet-400',
};
function LeaderboardCard({ leaderboard }) {
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3, 10);
  // Reorder for podium: 2 · 1 · 3
  const podium = [top3[1], top3[0], top3[2]].filter(Boolean);
  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden flex flex-col" data-testid="reports-leaderboard">
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="display text-[15px] font-bold tracking-tight text-neutral-900 inline-flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-500" strokeWidth={2.25} /> Top LO Leaderboard
        </div>
        <button className="h-7 px-2.5 rounded-md border hairline bg-white text-[11px] font-semibold text-neutral-700 hover:border-neutral-400 transition inline-flex items-center gap-1">
          By Volume <ChevronDown className="h-3 w-3" />
        </button>
      </div>
      {podium.length === 0 ? (
        <EmptyHint label="No funded loans yet in this period." />
      ) : (
        <div className="px-5 pt-2 pb-4 grid grid-cols-3 gap-2">
          {podium.map((lo, idx) => {
            const realRank = lo === top3[0] ? 1 : lo === top3[1] ? 2 : 3;
            const elev = realRank === 1 ? 'translate-y-0' : 'translate-y-3';
            return (
              <PodiumCard key={lo.id} lo={lo} rank={realRank} elev={elev} />
            );
          })}
        </div>
      )}
      {rest.length > 0 && (
        <div className="border-t hairline px-5 py-2 divide-y divide-neutral-100">
          {rest.map((lo, i) => (
            <div key={lo.id} data-testid={`reports-leaderboard-rank-${i + 4}`} className="grid grid-cols-[40px_1fr_auto_auto_auto] items-center gap-3 py-2">
              <span className="text-[12.5px] font-bold text-neutral-400">#{i + 4}</span>
              <span className="text-[13px] font-semibold text-neutral-900 truncate">{lo.name}</span>
              <span className="display text-[13px] font-bold text-neutral-900 tabular-nums">{money(lo.volume)}</span>
              <span className="text-[11.5px] text-neutral-500 tabular-nums">{lo.loans} Loans</span>
              <span className="text-[12px] font-bold text-emerald-500 tabular-nums w-12 text-right">{lo.pullThrough}%</span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t hairline px-5 py-3 text-center">
        <button className="text-[12px] font-bold text-sky-600 hover:underline">View Full Leaderboard</button>
      </div>
    </div>
  );
}

function PodiumCard({ lo, rank, elev }) {
  const isFirst = rank === 1;
  const rankBg = rank === 1 ? 'bg-amber-400 text-amber-950' : rank === 2 ? 'bg-neutral-300 text-neutral-800' : 'bg-orange-400 text-orange-950';
  return (
    <div className={cn('relative rounded-xl border hairline bg-neutral-50/60 px-3 py-3 text-center transition', elev,
      isFirst && 'ring-1 ring-amber-400/60 shadow-[0_8px_24px_-12px_rgba(251,191,36,0.5)]')}>
      <div className={cn('absolute -top-2.5 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full font-bold text-[11px] flex items-center justify-center shadow', rankBg)}>{rank}</div>
      <div className={cn('mx-auto h-12 w-12 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[12px] font-bold display ring-2 ring-offset-2 ring-offset-white', TONE_RING[lo.tone] || TONE_RING.ink)}>
        {lo.initials}
      </div>
      <div className="mt-2 text-[11.5px] font-semibold text-neutral-700 truncate">{lo.name}</div>
      <div className="display text-[15px] font-bold text-emerald-500 tabular-nums mt-0.5 leading-none">{money(lo.volume)}</div>
      <div className="text-[10px] text-neutral-400 mt-1 tabular-nums">{lo.loans} Loans · {lo.pullThrough}% PT</div>
    </div>
  );
}

/* =================================================================== */
/* Volume Trend — Apple Stocks / Linear-style premium chart.            */
/*   • Dynamic Y-axis scaled to the data (no fixed ceiling)             */
/*   • Single neon-green line with subtle drop-shadow glow              */
/*   • Soft gradient fade UNDER the line only (no big green block)      */
/*   • Dashed neutral last-month underlay                               */
/*   • Glass-style tooltip                                              */
/* =================================================================== */
function TrendTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  const primary = payload.find((p) => p.dataKey === 'volume');
  const compare = payload.find((p) => p.dataKey === 'lastMonth');
  if (!primary) return null;
  return (
    <div
      className="rounded-xl px-3 py-2 text-[12px] shadow-[0_18px_48px_-18px_rgba(0,0,0,0.5)]"
      style={{
        background: isDark ? 'rgba(15,15,17,0.85)' : 'rgba(255,255,255,0.92)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <div className={cn('text-[10px] uppercase tracking-wider font-bold', isDark ? 'text-neutral-400' : 'text-neutral-500')}>{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(34,197,94,0.7)]" />
        <span className="display font-bold tabular-nums text-emerald-400 text-[13px]">${Number(primary.value).toFixed(2)}M</span>
      </div>
      {compare && (
        <div className="flex items-center gap-2 mt-0.5">
          <span className={cn('h-[2px] w-3 rounded-full', isDark ? 'bg-neutral-500' : 'bg-neutral-400')} />
          <span className={cn('tabular-nums text-[11px] font-semibold', isDark ? 'text-neutral-400' : 'text-neutral-500')}>${Number(compare.value).toFixed(2)}M last mo</span>
        </div>
      )}
    </div>
  );
}

function VolumeTrendCard({ data, isDark, peak }) {
  // Dynamic Y-axis scaling — 25% headroom above the higher of the two series.
  const yAxis = useMemo(() => {
    const maxValue = Math.max(
      ...data.map((d) => Math.max(d.volume || 0, d.lastMonth || 0)),
      0.1,
    );
    const yAxisMax = Math.ceil(maxValue * 1.25);
    // Nice-looking step ladder so the labels read cleanly.
    let step;
    if (yAxisMax >= 50) step = 10;
    else if (yAxisMax >= 20) step = 5;
    else if (yAxisMax >= 8) step = 2;
    else if (yAxisMax >= 3) step = 1;
    else step = 0.5;
    const top = Math.ceil(yAxisMax / step) * step;
    const ticks = [];
    for (let v = 0; v <= top; v += step) ticks.push(Math.round(v * 100) / 100);
    return { top, ticks };
  }, [data]);

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden flex flex-col" data-testid="reports-volume-trend">
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="display text-[15px] font-bold tracking-tight text-neutral-900">Loan Volume Trend</div>
        <button className="h-7 px-2.5 rounded-md border hairline bg-white text-[11px] font-semibold text-neutral-700 hover:border-neutral-400 transition inline-flex items-center gap-1">
          Daily <ChevronDown className="h-3 w-3" />
        </button>
      </div>
      <div className="px-5 pb-2 flex items-center gap-4">
        <Legend dot="#22c55e" label="Volume (M)" />
        <Legend dot="#a3a3a3" dashed label="Last Month (M)" />
      </div>
      <div className="px-3 pb-4 h-[420px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 32, right: 28, bottom: 8, left: 12 }}>
            <defs>
              {/* Tight, dialed-back gradient — the line is the hero, the
                  fill just whispers a base reference. ~20% lighter than
                  the previous pass. */}
              <linearGradient id="trend-line-fade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#22c55e" stopOpacity={0.18} />
                <stop offset="45%" stopColor="#22c55e" stopOpacity={0.04} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
              {/* Refined glow — softer halo. Apple Stocks vibe, not casino. */}
              <filter id="line-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2.25" result="blur" />
                <feFlood floodColor="#22c55e" floodOpacity="0.32" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge>
                  <feMergeNode in="shadow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid
              strokeDasharray="2 6"
              stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: isDark ? '#737373' : '#a3a3a3', fontSize: 11, fontFamily: 'Satoshi, sans-serif' }}
              interval="preserveStartEnd"
              minTickGap={40}
              axisLine={false}
              tickLine={false}
              padding={{ left: 6, right: 6 }}
              dy={6}
            />
            <YAxis
              ticks={yAxis.ticks}
              tickFormatter={(v) => `$${v}M`}
              tick={{ fill: isDark ? '#737373' : '#a3a3a3', fontSize: 11, fontFamily: 'Satoshi, sans-serif' }}
              width={52}
              axisLine={false}
              tickLine={false}
              domain={[0, yAxis.top]}
            />
            <Tooltip
              content={<TrendTooltip isDark={isDark} />}
              cursor={false}
            />
            {/* Last month — slightly more visible dashed reference */}
            <Area
              type="natural"
              dataKey="lastMonth"
              stroke={isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.28)'}
              strokeWidth={1.5}
              strokeDasharray="4 5"
              fill="transparent"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
            {/* Primary cumulative volume — cinematic curve w/ refined glow */}
            <Area
              type="natural"
              dataKey="volume"
              stroke="#22c55e"
              strokeWidth={2.5}
              fill="url(#trend-line-fade)"
              filter="url(#line-glow)"
              dot={false}
              activeDot={{ r: 4.5, stroke: isDark ? '#0a0a0a' : '#fff', strokeWidth: 2, fill: '#22c55e' }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
        {/* Premium peak chip — glass-style, mirrors tooltip */}
        {peak && peak.volume > 0 && (
          <div
            className="absolute top-4 right-6 rounded-xl px-3 py-1.5 text-[11px] shadow-[0_18px_48px_-18px_rgba(0,0,0,0.5)]"
            style={{
              background: isDark ? 'rgba(15,15,17,0.85)' : 'rgba(255,255,255,0.92)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)'}`,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
          >
            <div className={cn('text-[10px] uppercase tracking-wider font-bold leading-tight', isDark ? 'text-neutral-400' : 'text-neutral-500')}>{peak.label}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(34,197,94,0.7)]" />
              <span className="display font-bold tabular-nums text-emerald-400 text-[13px]">${peak.volume.toFixed(2)}M</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function Legend({ dot, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
      {dashed
        ? <span className="h-[2px] w-4 rounded-full border-t border-dashed" style={{ borderColor: dot }} />
        : <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot }} />}
      {label}
    </span>
  );
}

/* =================================================================== */
/* Volume by State                                                      */
/* =================================================================== */
function VolumeByStateCard({ rows, topStates, isDark }) {
  // Heatmap reads `response_rate` by default; we feed it `volume` since
  // that's what the broker wants ranked.
  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden flex flex-col" data-testid="reports-volume-by-state">
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="display text-[15px] font-bold tracking-tight text-neutral-900">Volume by State</div>
        <button className="h-7 px-2.5 rounded-md border hairline bg-white text-[11px] font-semibold text-neutral-700 hover:border-neutral-400 transition inline-flex items-center gap-1">
          By Volume <ChevronDown className="h-3 w-3" />
        </button>
      </div>
      <div className="px-2">
        <StateHeatmap rows={rows} metric="volume" height={280} theme={isDark ? 'dark' : 'light'} />
      </div>
      <div className="px-5 pt-2 pb-1 grid grid-cols-[1fr_auto_auto] gap-x-3 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500">
        <span>Top States</span><span className="text-right">Volume</span><span className="text-right">Units</span>
      </div>
      <div className="px-5 pb-3 divide-y divide-neutral-100">
        {topStates.length === 0 ? (
          <EmptyHint label="No volume by state yet." />
        ) : topStates.map((s, i) => (
          <div key={s.state} className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-1.5 items-center text-[12.5px]">
            <span className="text-neutral-700 font-semibold">{i + 1}. {STATE_NAMES[s.state] || s.state}</span>
            <span className="text-right text-neutral-900 font-bold tabular-nums">{money(s.volume)}</span>
            <span className="text-right text-neutral-500 tabular-nums w-8">{s.funded}</span>
          </div>
        ))}
      </div>
      <div className="border-t hairline px-5 py-3 text-center">
        <button className="text-[12px] font-bold text-sky-600 hover:underline">View Full Breakdown</button>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Loans Submitted feed                                                 */
/* =================================================================== */
function LoansSubmittedCard({ rows, team, fullView }) {
  // Sort state — defaults to submittedAt DESC (newest first) which matches the
  // pre-sorted incoming list. Click a header to override.
  const [sort, setSort] = useState({ key: 'submittedAt', dir: 'desc' });
  // Column blueprint. Order: Broker | Client | Lender | Loan # | Amount |
  // State | Lead Source | Closed Date | Funded Date | Status. Each column
  // declares its sort behaviour & getter so the header click handler is
  // generic.
  const colDefs = [
    { key: 'broker',     label: 'Broker',      align: 'left',   width: 'minmax(140px,1.4fr)', sortable: true,  defaultDir: 'asc',
      get: (l) => (team.find((t) => t.id === l.assignedTo)?.name || 'Unassigned') },
    { key: 'client',     label: 'Client',      align: 'left',   width: 'minmax(140px,1.5fr)', sortable: true,  defaultDir: 'asc',
      get: (l) => l.name || '' },
    { key: 'lender',     label: 'Lender',      align: 'left',   width: 'minmax(110px,1.2fr)', sortable: true,  defaultDir: 'asc',
      get: (l) => l.application?.lender || l.lender || '' },
    { key: 'loanNumber', label: 'Loan #',      align: 'left',   width: 'minmax(110px,1.1fr)', sortable: true,  defaultDir: 'asc',
      get: (l) => l.loanNumber || '' },
    { key: 'amount',     label: 'Amount',      align: 'right',  width: '110px',               sortable: true,  defaultDir: 'desc',
      get: (l) => Number(l.estAmount) || 0 },
    { key: 'state',      label: 'State',       align: 'center', width: '60px',                sortable: true,  defaultDir: 'asc',
      get: (l) => leadStateAbbr(l) || '' },
    { key: 'source',     label: 'Lead Source', align: 'left',   width: 'minmax(120px,1.2fr)', sortable: true,  defaultDir: 'asc',
      get: (l) => (l.source || '').split('·')[0].trim() },
    { key: 'closedDate', label: 'Closed Date', align: 'left',   width: '100px',               sortable: true,  defaultDir: 'desc',
      get: (l) => l.closingDate || l.estimatedClosingDate || '' },
    { key: 'fundedDate', label: 'Funded Date', align: 'left',   width: '100px',               sortable: true,  defaultDir: 'desc',
      get: (l) => l.fundingDate || l.fundedAt || '' },
    { key: 'status',     label: 'Status',      align: 'center', width: '140px',               sortable: true,  defaultDir: 'asc',
      get: (l) => (STAGE_BY_KEY[normalizeStageKey(l.loanStatus)]?.label || 'Submitted') },
  ];
  const cols = colDefs.map((c) => c.width).join(' ');

  const display = useMemo(() => {
    const colDef = colDefs.find((c) => c.key === sort.key);
    // submittedAt isn't a column, but it's our natural default — when nobody
    // has overridden the sort we keep the prebuilt newest-first order.
    if (!colDef) return rows;
    const get = colDef.get;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      // Date strings sort lexically when ISO; "" sinks to the bottom.
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      return String(av).localeCompare(String(bv)) * dir;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  const cycleSort = (key, defaultDir) => {
    setSort((p) => p.key === key
      ? { key, dir: p.dir === defaultDir ? (defaultDir === 'desc' ? 'asc' : 'desc') : defaultDir }
      : { key, dir: defaultDir });
  };

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="reports-loans-submitted">
      <div className="px-5 py-3.5 flex items-center justify-between border-b hairline">
        <div>
          <div className="display text-[15px] font-bold tracking-tight text-neutral-900 inline-flex items-center gap-2">
            <FileText className="h-4 w-4 text-neutral-500" /> {fullView ? 'All Loans Submitted' : 'Loans Submitted'}
          </div>
          {fullView && (
            <div className="text-[10.5px] text-neutral-400 dark:text-neutral-500 mt-0.5 leading-tight">
              Sorted by date promoted to <span className="font-semibold text-neutral-500 dark:text-neutral-400">Ready to be Submitted</span>, filtered by the selected calendar range.
            </div>
          )}
        </div>
        {fullView ? (
          <span className="text-[11px] font-semibold text-neutral-500 tabular-nums">{display.length} {display.length === 1 ? 'loan' : 'loans'}</span>
        ) : (
          <Link to="/pipeline" className="text-[12px] font-semibold text-sky-600 hover:underline inline-flex items-center gap-1">
            View All <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div
        className="grid gap-3 px-5 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border-b hairline bg-neutral-50/40"
        style={{ gridTemplateColumns: cols }}
      >
        {colDefs.map((c) => {
          const active = sort.key === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => cycleSort(c.key, c.defaultDir)}
              data-testid={`loans-sort-${c.key}`}
              className={cn(
                'inline-flex items-center gap-1 select-none transition-colors',
                c.align === 'right'  && 'justify-end',
                c.align === 'center' && 'justify-center',
                c.align === 'left'   && 'justify-start',
                'hover:text-neutral-900',
              )}
            >
              {c.label}
              <span className={cn('text-[8.5px] leading-none', active ? 'text-neutral-900' : 'text-neutral-300')}>
                {active ? (sort.dir === 'asc' ? '▲' : '▼') : '◇'}
              </span>
            </button>
          );
        })}
      </div>
      {display.length === 0 ? (
        <EmptyHint label="No submissions yet in this window." />
      ) : (
        <div className="divide-y divide-neutral-100">
          {display.map((l) => <LoanSubmittedRow key={l.id} lead={l} team={team} cols={cols} />)}
        </div>
      )}
    </div>
  );
}
// Full-precision money formatter ($475,000 not $475K) for in-row amounts.
function moneyFull(n) {
  return `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
}
// Localized date — short month/day/year. Returns "—" when missing.
function fmtRowDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}
function LoanSubmittedRow({ lead, team, cols }) {
  const lo = team.find((t) => t.id === lead.assignedTo);
  const status = STAGE_BY_KEY[normalizeStageKey(lead.loanStatus)] || STAGE_DEFS[0];
  const StatusIcon = status.Icon;
  const state = leadStateAbbr(lead) || '—';
  const lender = lead.application?.lender || lead.lender || '—';
  const sourceRaw = (lead.source || '').trim();
  const leadSource = sourceRaw.split('·')[0].trim() || '—';
  const closedDate = fmtRowDate(lead.closingDate || lead.estimatedClosingDate);
  const fundedDate = fmtRowDate(lead.fundingDate || lead.fundedAt);
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="grid gap-3 px-5 py-3 items-center hover:bg-neutral-50/60 transition-colors"
      style={{ gridTemplateColumns: cols }}
    >
      <div className="text-[12.5px] font-semibold text-neutral-700 truncate" title={lo?.name || 'Unassigned'}>
        {lo?.name || 'Unassigned'}
      </div>
      <div className="text-[12.5px] text-neutral-700 truncate">{lead.name}</div>
      <div className="text-[12.5px] text-neutral-700 truncate" title={lender}>{lender}</div>
      <div className="text-[12px] text-neutral-500 truncate tabular-nums">{lead.loanNumber || '—'}</div>
      <div className="text-right text-[12.5px] font-bold text-neutral-900 tabular-nums">{moneyFull(lead.estAmount)}</div>
      <div className="text-center text-[12px] font-semibold text-neutral-700 tabular-nums">{state}</div>
      <div className="text-[12.5px] text-neutral-700 truncate" title={sourceRaw}>{leadSource}</div>
      <div className="text-[12px] text-neutral-500 tabular-nums">{closedDate}</div>
      <div className="text-[12px] text-neutral-500 tabular-nums">{fundedDate}</div>
      <div className="flex justify-center">
        <span
          className={cn(
            'inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold ring-1 w-full max-w-[132px] leading-tight text-center',
            status.tone.bg, status.tone.text, status.tone.ring,
          )}
        >
          <StatusIcon className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span className="truncate">{status.label}</span>
        </span>
      </div>
    </Link>
  );
}

function EmptyHint({ label }) {
  return (
    <div className="px-5 py-6 text-center text-[12px] text-neutral-400 italic">{label}</div>
  );
}

/* =================================================================== */
/* Claims Tab — tracks lead claim activity (who's pulling leads out of */
/* the pool and at what rate). Pivots on `updated_at` of any lead with */
/* `assignedTo` set so the calendar/filter chips drive the window.      */
/* =================================================================== */
function ClaimsTab({ range, allLeads, filters, team }) {
  const ms = range.start.getTime();
  const me = range.end.getTime() + 24 * 60 * 60 * 1000 - 1;

  // Per-period claim corpus. A "claim" here = a lead that has an owner
  // and whose ownership stamp (claimedAt → updated_at → created_at) falls
  // inside the window. Unclaimed leads in the pool are tracked separately.
  const claims = useMemo(() => {
    const pivot = (l) => {
      const t = new Date(l.claimedAt || l.updated_at || l.created_at).getTime();
      return isFinite(t) ? t : null;
    };
    return allLeads.filter((l) => {
      if (!l.assignedTo) return false;
      const t = pivot(l);
      if (t === null || t < ms || t > me) return false;
      // Same filter dimensions as the rest of the page.
      if (filters.loIds.size && !filters.loIds.has(l.assignedTo)) return false;
      if (filters.sources.size) {
        const src = (l.source || '').split('·')[0].trim();
        if (!filters.sources.has(src)) return false;
      }
      if (filters.states.size) {
        const st = leadStateAbbr(l);
        if (!st || !filters.states.has(st)) return false;
      }
      return true;
    });
  }, [allLeads, ms, me, filters]);

  // Unclaimed pool snapshot — leads still sitting in the search pool
  // (no `assignedTo`). Surfaces how much inventory is waiting to be
  // worked vs. how much got claimed in the window.
  const unclaimedPool = useMemo(() =>
    allLeads.filter((l) => !l.assignedTo).length
  , [allLeads]);

  // Per-LO claim leaderboard. `convertedClosed` counts the subset of
  // claims that have already hit a funded status — a quick read on
  // claim quality, not just velocity.
  const byLO = useMemo(() => {
    const map = new Map();
    claims.forEach((l) => {
      const id = l.assignedTo;
      const row = map.get(id) || { id, total: 0, hot: 0, closed: 0, withdrawn: 0, denied: 0 };
      row.total += 1;
      if (l.temp === 'hot') row.hot += 1;
      if (l.loanStatus === 'funded') row.closed += 1;
      if (l.withdrawn) row.withdrawn += 1;
      if (l.denied) row.denied += 1;
      map.set(id, row);
    });
    const rows = Array.from(map.values())
      .map((r) => {
        const m = team.find((t) => t.id === r.id);
        return { ...r, name: m?.name || 'Unassigned', initials: m?.initials || 'LO' };
      })
      .sort((a, b) => b.total - a.total);
    return rows;
  }, [claims, team]);

  // Lead-source breakdown — which mailer / channel is feeding the pool
  // most aggressively right now.
  const bySource = useMemo(() => {
    const map = new Map();
    claims.forEach((l) => {
      const src = (l.source || 'Unknown').split('·')[0].trim() || 'Unknown';
      map.set(src, (map.get(src) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count);
  }, [claims]);

  // KPI counts.
  const totalClaimed = claims.length;
  const hotClaimed = claims.filter((l) => l.temp === 'hot').length;
  const closedClaimed = claims.filter((l) => l.loanStatus === 'funded').length;
  const claimToCloseRate = totalClaimed ? Math.round((closedClaimed / totalClaimed) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="claims-kpis">
        <KpiTile label="Total Claimed"        value={totalClaimed.toString()}    change=""   tone="emerald" Icon={Hand}       series={sparkFromCount(claims)} />
        <KpiTile label="Hot Claimed"          value={hotClaimed.toString()}      change=""   tone="rose"    Icon={Flag}       series={sparkFromCount(claims.filter((l) => l.temp === 'hot'))} />
        <KpiTile label="Claim → Close Rate"   value={`${claimToCloseRate}%`}     change=""   tone="amber"   Icon={Percent}    series={sparkFromCount(claims.filter((l) => l.loanStatus === 'funded'))} />
        <KpiTile label="Unclaimed Pool"       value={unclaimedPool.toString()}   change=""   tone="blue"    Icon={ClipboardList} series={sparkFromCount(allLeads.filter((l) => !l.assignedTo))} />
      </div>

      {/* Claims by LO + Source split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border hairline bg-white overflow-hidden" data-testid="claims-by-lo">
          <div className="px-5 py-3.5 flex items-center justify-between border-b hairline">
            <div className="display text-[15px] font-bold tracking-tight text-neutral-900 inline-flex items-center gap-2">
              <Hand className="h-4 w-4 text-neutral-500" /> Claims by LO
            </div>
            <span className="text-[11px] font-semibold text-neutral-500 tabular-nums">{byLO.length} LO{byLO.length === 1 ? '' : 's'}</span>
          </div>
          <div className="grid grid-cols-[2fr_repeat(5,1fr)] gap-3 px-5 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border-b hairline bg-neutral-50/40">
            <div>Loan Officer</div>
            <div className="text-right">Claimed</div>
            <div className="text-right">Hot</div>
            <div className="text-right">Closed</div>
            <div className="text-right">Withdrawn</div>
            <div className="text-right">Denied</div>
          </div>
          {byLO.length === 0 ? (
            <EmptyHint label="No claims yet in this window." />
          ) : (
            <div className="divide-y divide-neutral-100">
              {byLO.map((r) => (
                <div key={r.id} className="grid grid-cols-[2fr_repeat(5,1fr)] gap-3 px-5 py-2.5 items-center text-[12.5px]">
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-neutral-900 text-white text-[10px] font-bold flex items-center justify-center">{r.initials}</div>
                    <span className="font-semibold text-neutral-900 truncate">{r.name}</span>
                  </div>
                  <div className="text-right font-bold text-neutral-900 tabular-nums">{r.total}</div>
                  <div className="text-right font-semibold text-rose-600 tabular-nums">{r.hot}</div>
                  <div className="text-right font-semibold text-emerald-600 tabular-nums">{r.closed}</div>
                  <div className="text-right text-neutral-500 tabular-nums">{r.withdrawn}</div>
                  <div className="text-right text-neutral-500 tabular-nums">{r.denied}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid="claims-by-source">
          <div className="px-5 py-3.5 border-b hairline">
            <div className="display text-[15px] font-bold tracking-tight text-neutral-900 inline-flex items-center gap-2">
              <FileText className="h-4 w-4 text-neutral-500" /> Claims by Source
            </div>
          </div>
          {bySource.length === 0 ? (
            <EmptyHint label="No claims yet." />
          ) : (
            <div className="divide-y divide-neutral-100">
              {bySource.map((r) => {
                const pct = totalClaimed ? Math.round((r.count / totalClaimed) * 100) : 0;
                return (
                  <div key={r.source} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-neutral-900 truncate">{r.source}</div>
                      <div className="mt-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[13px] font-bold text-neutral-900 tabular-nums">{r.count}</div>
                      <div className="text-[10px] text-neutral-400 tabular-nums">{pct}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* =================================================================== */
/* Lead Source tab — two stacked cards (Loan Type + Lead Source).      */
/* Each row shows Submissions count + volume and Closings count +      */
/* volume. Rows are sorted by submission count desc; totals footer.   */
/* =================================================================== */
function LeadSourcesTab({ submitted, funded }) {
  // Canonical loan-purpose labels mirror Leads.jsx so legacy values
  // ("Refi", "Cash Out Refi") roll up under the same bucket.
  const normalizePurpose = (raw) => {
    if (!raw) return 'Unspecified';
    const lc = String(raw).trim().toLowerCase();
    if (['refi', 'rate & term refi', 'refinance', 'rate and term refi', 'refinance r&t'].includes(lc)) return 'Refinance R&T';
    if (['cash out refi', 'cash-out refi', 'cash out refinance'].includes(lc)) return 'Cash Out Refinance';
    if (['heloc', 'home equity (heloc)'].includes(lc)) return 'HELOC';
    if (lc === 'home equity loan') return 'Home Equity Loan';
    if (lc === 'purchase') return 'Purchase';
    return String(raw).trim();
  };
  const purposeOf = (l) => normalizePurpose(l.application?.loanPurpose || l.loanIntent);
  const sourceOf  = (l) => ((l.source || '').split('·')[0].trim()) || 'Unspecified';

  // Build a {key -> {subs, subsVol, closings, closedVol}} aggregate.
  const aggregate = (keyFn, list) => {
    const map = new Map();
    list.forEach((l) => {
      const k = keyFn(l);
      const cur = map.get(k) || { key: k, subs: 0, subsVol: 0, closings: 0, closedVol: 0 };
      cur.subs += 1;
      cur.subsVol += Number(l.estAmount) || 0;
      map.set(k, cur);
    });
    return map;
  };
  const tally = (map, keyFn, list, field, volField) => {
    list.forEach((l) => {
      const k = keyFn(l);
      const cur = map.get(k) || { key: k, subs: 0, subsVol: 0, closings: 0, closedVol: 0 };
      cur[field]    += 1;
      cur[volField] += Number(l.estAmount) || 0;
      map.set(k, cur);
    });
    return map;
  };

  const purposeMap = aggregate(purposeOf, submitted);
  tally(purposeMap, purposeOf, funded, 'closings', 'closedVol');
  const purposeRows = Array.from(purposeMap.values()).sort((a, b) => b.subsVol - a.subsVol);

  const sourceMap = aggregate(sourceOf, submitted);
  tally(sourceMap, sourceOf, funded, 'closings', 'closedVol');
  const sourceRows = Array.from(sourceMap.values()).sort((a, b) => b.subsVol - a.subsVol);

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="sources-tab">
      <BreakdownCard
        title="Loan Type"
        Icon={Layers}
        accent="violet"
        rows={purposeRows}
        testid="breakdown-loan-type"
      />
      <BreakdownCard
        title="Lead Source"
        Icon={Crosshair}
        accent="sky"
        rows={sourceRows}
        testid="breakdown-lead-source"
      />
    </div>
  );
}

function BreakdownCard({ title, Icon, accent, rows, testid }) {
  const accents = {
    violet: 'text-violet-500 dark:text-violet-400',
    sky:    'text-sky-500 dark:text-sky-400',
  };
  const accentColor = accents[accent] || accents.sky;
  const totals = rows.reduce((acc, r) => ({
    subs: acc.subs + r.subs,
    subsVol: acc.subsVol + r.subsVol,
    closings: acc.closings + r.closings,
    closedVol: acc.closedVol + r.closedVol,
  }), { subs: 0, subsVol: 0, closings: 0, closedVol: 0 });

  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b hairline inline-flex items-center gap-2.5">
        <Icon className={cn('h-4 w-4', accentColor)} />
        <span className="display text-[16px] font-bold tracking-tight text-neutral-900 dark:text-white">{title}</span>
      </div>
      <div
        className="grid gap-3 px-5 py-2.5 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 dark:text-neutral-400 border-b hairline bg-neutral-50/40 dark:bg-neutral-900/40"
        style={{ gridTemplateColumns: 'minmax(160px,1.4fr) 70px 100px 70px 100px' }}
      >
        <div>{title}</div>
        <div className="text-right">Subs</div>
        <div className="text-right">Sub Volume</div>
        <div className="text-right">Closings</div>
        <div className="text-right">Closed Volume</div>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-6 text-center text-[12.5px] text-neutral-500 dark:text-neutral-400">No data in this window.</div>
      ) : (
        <div className="divide-y divide-neutral-100 dark:divide-white/05">
          {rows.map((r) => (
            <div
              key={r.key}
              className="grid gap-3 px-5 py-2.5 items-center text-[12.5px] hover:bg-neutral-50/60 dark:hover:bg-neutral-900/40 transition"
              style={{ gridTemplateColumns: 'minmax(160px,1.4fr) 70px 100px 70px 100px' }}
            >
              <span className="font-semibold text-neutral-900 dark:text-white truncate" title={r.key}>{r.key}</span>
              <span className="text-right text-neutral-700 dark:text-neutral-300 tabular-nums">{r.subs.toLocaleString('en-US')}</span>
              <span className="text-right text-emerald-500 dark:text-emerald-400 font-semibold tabular-nums">{moneyCompact(r.subsVol)}</span>
              <span className="text-right text-neutral-700 dark:text-neutral-300 tabular-nums">{r.closings.toLocaleString('en-US')}</span>
              <span className="text-right text-emerald-500 dark:text-emerald-400 font-semibold tabular-nums">{moneyCompact(r.closedVol)}</span>
            </div>
          ))}
        </div>
      )}
      <div
        className="grid gap-3 px-5 py-3 items-center text-[12.5px] border-t hairline bg-neutral-50/50 dark:bg-neutral-900/50"
        style={{ gridTemplateColumns: 'minmax(160px,1.4fr) 70px 100px 70px 100px' }}
      >
        <span className="font-bold text-neutral-900 dark:text-white uppercase tracking-wide text-[11px]">Total</span>
        <span className="text-right font-bold text-neutral-900 dark:text-white tabular-nums">{totals.subs.toLocaleString('en-US')}</span>
        <span className="text-right font-bold text-emerald-500 dark:text-emerald-400 tabular-nums">{moneyCompact(totals.subsVol)}</span>
        <span className="text-right font-bold text-neutral-900 dark:text-white tabular-nums">{totals.closings.toLocaleString('en-US')}</span>
        <span className="text-right font-bold text-emerald-500 dark:text-emerald-400 tabular-nums">{moneyCompact(totals.closedVol)}</span>
      </div>
    </div>
  );
}

// Compact money — $1.2M, $717k, $221k. Keeps the report tables breathable.
function moneyCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${Math.round(v / 1_000)}k`;
  return `$${v.toLocaleString('en-US')}`;
}


/* =================================================================== */
/* Outcomes Tab — Withdrawn / Denied / DNC bucket tracking. Single      */
/* dropdown picks the outcome; second dropdown scopes by LO; calendar  */
/* range chip in the header drives the window. Includes a one-click   */
/* CSV export so the broker can drop the list straight into Excel.    */
/* =================================================================== */
function OutcomesTab({ range, allLeads, filters, team }) {
  const [outcome, setOutcome] = useState('withdrawn'); // 'withdrawn' | 'denied' | 'dnc'

  const ms = range.start.getTime();
  const me = range.end.getTime() + 24 * 60 * 60 * 1000 - 1;

  const inWindow = (l) => {
    const pivot = l.withdrawnAt || l.deniedAt || l.updated_at || l.created_at;
    if (!pivot) return false;
    const t = new Date(pivot).getTime();
    return isFinite(t) && t >= ms && t <= me;
  };
  // Honour the calendar/LO/source/state chips from the header. LO scoping
  // lives in the global Filters popover (Loan Officers group) so the
  // Outcomes tab doesn't double up the control.
  const passesGlobalFilters = (l) => {
    if (filters.loIds.size && !filters.loIds.has(l.assignedTo || 'unassigned')) return false;
    if (filters.sources.size) {
      const src = (l.source || '').split('·')[0].trim();
      if (!filters.sources.has(src)) return false;
    }
    if (filters.states.size) {
      const st = leadStateAbbr(l);
      if (!st || !filters.states.has(st)) return false;
    }
    return true;
  };

  // The outcome predicate flips with the dropdown. We snapshot the count
  // for all three buckets so the dropdown can show the matching size
  // next to each option (e.g. "Denied (4)").
  const matchOutcome = (l, key) => {
    if (key === 'withdrawn') return !!l.withdrawn;
    if (key === 'denied')    return !!l.denied;
    if (key === 'dnc')       return l.temp === 'dnc';
    return false;
  };

  const counts = useMemo(() => {
    const base = allLeads.filter((l) => inWindow(l) && passesGlobalFilters(l));
    return {
      withdrawn: base.filter((l) => matchOutcome(l, 'withdrawn')).length,
      denied:    base.filter((l) => matchOutcome(l, 'denied')).length,
      dnc:       base.filter((l) => matchOutcome(l, 'dnc')).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLeads, ms, me, filters]);

  const rows = useMemo(() => {
    return allLeads
      .filter((l) => matchOutcome(l, outcome) && inWindow(l) && passesGlobalFilters(l))
      .sort((a, b) => {
        const at = new Date(a.withdrawnAt || a.deniedAt || a.updated_at || 0).getTime();
        const bt = new Date(b.withdrawnAt || b.deniedAt || b.updated_at || 0).getTime();
        return bt - at;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLeads, ms, me, outcome, filters]);

  const totalLeads = useMemo(() =>
    allLeads.filter((l) => inWindow(l) && passesGlobalFilters(l)).length
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [allLeads, ms, me, filters]);

  const outcomeMeta = {
    withdrawn: { label: 'Withdrawn', Icon: Archive,  tone: 'amber' },
    denied:    { label: 'Denied',    Icon: ShieldX,  tone: 'rose' },
    dnc:       { label: 'Opt Out',   Icon: PhoneOff, tone: 'blue' },
  }[outcome];

  return (
    <div className="space-y-5">
      {/* Inline controls — Outcome + LO + Export. The global Filters chip
          in the header still applies on top of these for advanced cuts. */}
      <div className="flex flex-wrap items-center gap-3" data-testid="outcomes-controls">
        <OutcomePicker outcome={outcome} setOutcome={setOutcome} counts={counts} />
        <div className="flex-1" />
        <button
          data-testid="outcomes-export-csv"
          onClick={() => exportOutcomesCsv({ outcome, outcomeLabel: outcomeMeta.label, rows, team })}
          disabled={!rows.length}
          className={cn(
            'h-10 px-4 rounded-xl text-[12.5px] font-bold inline-flex items-center gap-2 transition',
            rows.length
              ? 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800'
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed',
          )}
        >
          <FileText className="h-3.5 w-3.5" />
          Export to Excel ({rows.length})
        </button>
      </div>

      {/* KPI tiles — Total outcome + 3 bucket counts for quick context. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="outcomes-kpis">
        <KpiTile label="Total Leads" value={totalLeads.toString()}            change="" tone="neutral" Icon={ClipboardList} series={sparkFromCount(allLeads.filter(inWindow))} />
        <KpiTile label="Withdrawn"   value={counts.withdrawn.toString()}      change="" tone="amber"   Icon={Archive}       series={sparkFromCount(allLeads.filter((l) => l.withdrawn && inWindow(l)))} />
        <KpiTile label="Denied"      value={counts.denied.toString()}         change="" tone="rose"    Icon={ShieldX}       series={sparkFromCount(allLeads.filter((l) => l.denied && inWindow(l)))} />
        <KpiTile label="Opt Out"     value={counts.dnc.toString()}            change="" tone="blue"    Icon={PhoneOff}      series={sparkFromCount(allLeads.filter((l) => l.temp === 'dnc' && inWindow(l)))} />
      </div>

      {/* Single morphing table — DNC swaps to a full address view. */}
      <OutcomeReportTable outcome={outcome} meta={outcomeMeta} rows={rows} team={team} />
    </div>
  );
}

function OutcomePicker({ outcome, setOutcome, counts }) {
  const options = [
    { key: 'withdrawn', label: 'Withdrawn', Icon: Archive  },
    { key: 'denied',    label: 'Denied',    Icon: ShieldX  },
    { key: 'dnc',       label: 'Opt Out',   Icon: PhoneOff },
  ];
  return (
    <div className="rounded-xl border hairline bg-white p-1 inline-flex items-center gap-1" data-testid="outcomes-picker">
      {options.map((o) => {
        const active = outcome === o.key;
        return (
          <button
            key={o.key}
            data-testid={`outcomes-pick-${o.key}`}
            onClick={() => setOutcome(o.key)}
            className={cn(
              'h-9 px-3.5 rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-2 transition',
              active
                ? 'bg-neutral-900 text-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.3)]'
                : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50',
            )}
          >
            <o.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
            {o.label}
            <span className={cn('ml-0.5 text-[10.5px] font-bold tabular-nums', active ? 'text-white/80' : 'text-neutral-400')}>
              {counts[o.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function OutcomeReportTable({ outcome, meta, rows, team }) {
  const { Icon, label } = meta;
  const isDnc = outcome === 'dnc';
  // DNC report surfaces the full physical address — the broker uses this
  // for compliance scrubs / outbound suppression lists. First/Last name
  // are split so the CSV ports cleanly into mailer ops.
  // Withdrawn/Denied carry BOTH created date and action date so the
  // broker can read time-to-outcome at a glance.
  const cols = isDnc
    ? 'minmax(100px,1fr) minmax(100px,1fr) minmax(170px,2fr) minmax(100px,1.1fr) 60px 80px 100px'
    : 'minmax(140px,1.4fr) minmax(130px,1.2fr) minmax(120px,1.2fr) 60px 100px 100px';
  const actionLabel = outcome === 'denied' ? 'Denied' : 'Withdrawn';
  const headers = isDnc
    ? ['First Name', 'Last Name', 'Street Address', 'City', 'State', 'Zip', 'Date']
    : ['Client', 'Broker', outcome === 'denied' ? 'Reason' : 'Source', 'State', 'Created', `${actionLabel} Date`];

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden" data-testid={`outcomes-table-${outcome}`}>
      <div className="px-5 py-3.5 flex items-center justify-between border-b hairline">
        <div className="display text-[15px] font-bold tracking-tight text-neutral-900 inline-flex items-center gap-2">
          <Icon className="h-4 w-4 text-neutral-500" /> {label} Leads
        </div>
        <span className="text-[11px] font-semibold text-neutral-500 tabular-nums">{rows.length} {rows.length === 1 ? 'lead' : 'leads'}</span>
      </div>
      <div
        className="grid gap-3 px-5 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border-b hairline bg-neutral-50/40"
        style={{ gridTemplateColumns: cols }}
      >
        {headers.map((h, i) => (
          <div key={h} className={cn(
            // DNC: last col right-aligned, State + Zip centered.
            isDnc && i === headers.length - 1 ? 'text-right' :
            isDnc && (i === 4 || i === 5) ? 'text-center' :
            // Withdrawn/Denied: last 2 cols (date columns) right-aligned, State centered.
            !isDnc && i >= headers.length - 2 ? 'text-right' :
            !isDnc && i === 3 ? 'text-center' :
            '',
          )}>
            {h}
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <EmptyHint label={`No ${label.toLowerCase()} leads in this window.`} />
      ) : (
        <div className="divide-y divide-neutral-100 max-h-[560px] overflow-y-auto">
          {rows.map((l) => isDnc
            ? <DncRow key={l.id} lead={l} cols={cols} />
            : <WdRow  key={l.id} lead={l} cols={cols} team={team} outcome={outcome} />
          )}
        </div>
      )}
    </div>
  );
}

// Split a full name into [first, last]. Anything past the first token
// is captured as the last name so middle initials / suffixes stay
// attached (e.g. "Mary Jane Smith" → ["Mary", "Jane Smith"]).
function splitName(full) {
  const t = (full || '').trim();
  if (!t) return ['', ''];
  const idx = t.indexOf(' ');
  if (idx === -1) return [t, ''];
  return [t.slice(0, idx), t.slice(idx + 1)];
}

function DncRow({ lead, cols }) {
  const app = lead.application || {};
  const [first, last] = splitName(lead.name);
  const street = app.propertyAddress || app.currentAddress || '—';
  const city   = app.propertyCity    || app.currentCity    || '—';
  const stAbbr = app.propertyState   || app.currentState   || leadStateAbbr(lead) || '—';
  const zip    = app.propertyZip     || app.currentZip     || '—';
  const date   = lead.updated_at
    ? new Date(lead.updated_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
    : '—';
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="grid gap-3 px-5 py-2.5 items-center text-[12.5px] hover:bg-neutral-50/60 transition"
      style={{ gridTemplateColumns: cols }}
    >
      <span className="font-semibold text-neutral-900 truncate">{first || '—'}</span>
      <span className="font-semibold text-neutral-900 truncate">{last || '—'}</span>
      <span className="text-neutral-700 truncate" title={street}>{street}</span>
      <span className="text-neutral-700 truncate" title={city}>{city}</span>
      <span className="text-center text-neutral-700 font-semibold tabular-nums">{stAbbr}</span>
      <span className="text-center text-neutral-700 tabular-nums">{zip}</span>
      <span className="text-right text-neutral-500 tabular-nums">{date}</span>
    </Link>
  );
}

function WdRow({ lead, cols, team, outcome }) {
  const lo = team.find((t) => t.id === lead.assignedTo);
  // Created date — when the lead landed in the CRM.
  const createdStr = lead.created_at
    ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
    : '—';
  // Action date — when the lead was withdrawn/denied. Falls back to
  // updated_at if the explicit stamp is missing.
  const actionRaw = outcome === 'denied'
    ? (lead.deniedAt    || lead.updated_at)
    : (lead.withdrawnAt || lead.updated_at);
  const actionStr = actionRaw
    ? new Date(actionRaw).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
    : '—';
  const reason = outcome === 'denied'
    ? (lead.deniedReason || 'Unspecified')
    : ((lead.source || '').split('·')[0].trim() || '—');
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="grid gap-3 px-5 py-2.5 items-center text-[12.5px] hover:bg-neutral-50/60 transition"
      style={{ gridTemplateColumns: cols }}
    >
      <span className="font-semibold text-neutral-900 truncate">{lead.name}</span>
      <span className="text-neutral-700 truncate">{lo?.name || 'Unassigned'}</span>
      <span className="text-neutral-700 truncate" title={reason}>{reason}</span>
      <span className="text-center text-neutral-700 font-semibold tabular-nums">{leadStateAbbr(lead) || '—'}</span>
      <span className="text-right text-neutral-500 tabular-nums">{createdStr}</span>
      <span className="text-right text-neutral-900 font-semibold tabular-nums">{actionStr}</span>
    </Link>
  );
}

/* CSV export — opens as Excel on every modern desktop OS. Columns adapt
   to the active outcome (DNC carries the full address; Withdrawn/Denied
   carry the broker + reason). Filename embeds the bucket + today's date
   so multiple exports don't overwrite each other. */
function exportOutcomesCsv({ outcome, outcomeLabel, rows, team }) {
  if (!rows.length) return;
  const csvEscape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  let headers, body;
  if (outcome === 'dnc') {
    headers = ['First Name', 'Last Name', 'Street Address', 'City', 'State', 'Zip', 'Phone', 'Email', 'Broker', 'Date'];
    body = rows.map((l) => {
      const app = l.application || {};
      const lo = team.find((t) => t.id === l.assignedTo);
      const [first, last] = (() => {
        const t = (l.name || '').trim();
        if (!t) return ['', ''];
        const idx = t.indexOf(' ');
        return idx === -1 ? [t, ''] : [t.slice(0, idx), t.slice(idx + 1)];
      })();
      return [
        first,
        last,
        app.propertyAddress || app.currentAddress || '',
        app.propertyCity || app.currentCity || '',
        app.propertyState || app.currentState || leadStateAbbr(l) || '',
        app.propertyZip || app.currentZip || '',
        l.phone || '',
        l.email || '',
        lo?.name || 'Unassigned',
        l.updated_at ? new Date(l.updated_at).toISOString().slice(0, 10) : '',
      ];
    });
  } else {
    const actionCol = outcome === 'denied' ? 'Denied Date' : 'Withdrawn Date';
    headers = ['Client', 'Broker', outcome === 'denied' ? 'Reason' : 'Source', 'State', 'Loan Amount', 'Phone', 'Email', 'Created Date', actionCol];
    body = rows.map((l) => {
      const lo = team.find((t) => t.id === l.assignedTo);
      const reason = outcome === 'denied'
        ? (l.deniedReason || 'Unspecified')
        : ((l.source || '').split('·')[0].trim() || '');
      const actionRaw = outcome === 'denied' ? (l.deniedAt || l.updated_at) : (l.withdrawnAt || l.updated_at);
      return [
        l.name,
        lo?.name || 'Unassigned',
        reason,
        leadStateAbbr(l) || '',
        l.estAmount || '',
        l.phone || '',
        l.email || '',
        l.created_at ? new Date(l.created_at).toISOString().slice(0, 10) : '',
        actionRaw ? new Date(actionRaw).toISOString().slice(0, 10) : '',
      ];
    });
  }
  const csv = [headers, ...body].map((row) => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kinfolk-${outcomeLabel.toLowerCase()}-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

